import {config} from "@/constants/config";
import type {TResponseRequest} from "@/interfaces/http.interface";
import httpRequest from "@/providers/http/request.http";
import {buildQueryUrl} from "@/providers/http/request.instance";
import type {
  ChangeUserLevelBody,
  CountryLevelLimit,
  CreateCountryLevelLimitBody,
  LevelConfig,
  PromotionCondition,
  UpdateCountryLevelLimitBody,
  UpdateLevelConfigBody,
  UpdatePromotionConditionBody,
  UserLevel,
  UserLevelListResult,
} from "@/types/levels.types";
import {normalizePagination} from "@/utils/pagination";

/* ── User Levels ───────────────────────────────────────────── */

interface UserLevelFilters {
  page?: number;
  limit?: number;
  level?: number;
}

export const getUserLevelsService = async (
  filters: UserLevelFilters = {}
): Promise<TResponseRequest<UserLevelListResult>> => {
  const raw = await httpRequest.get<UserLevel[]>({
    url: buildQueryUrl(`${config.apiBaseUrl}/bo/levels/users`, filters as Record<string, unknown>),
  });
  if (!raw.data || !raw.pagination) return {...raw, data: undefined};
  return {...raw, data: {items: raw.data, pagination: normalizePagination(raw.pagination)}};
};

export const getUserLevelService = async (userId: string): Promise<TResponseRequest<UserLevel>> => {
  return httpRequest.get<UserLevel>({
    url: `${config.apiBaseUrl}/bo/levels/users/${encodeURIComponent(userId)}`,
  });
};

export const changeUserLevelService = async (
  userId: string,
  body: ChangeUserLevelBody
): Promise<TResponseRequest<UserLevel>> => {
  return httpRequest.patch<UserLevel>({
    url: `${config.apiBaseUrl}/bo/levels/users/${encodeURIComponent(userId)}/change`,
    body,
  });
};

/* ── Level Configs ─────────────────────────────────────────── */

export const getLevelConfigsService = async (): Promise<TResponseRequest<LevelConfig[]>> => {
  return httpRequest.get<LevelConfig[]>({url: `${config.apiBaseUrl}/bo/levels/configs`});
};

export const getLevelConfigService = async (level: number): Promise<TResponseRequest<LevelConfig>> => {
  return httpRequest.get<LevelConfig>({
    url: `${config.apiBaseUrl}/bo/levels/configs/${encodeURIComponent(String(level))}`,
  });
};

export const updateLevelConfigService = async (
  level: number,
  body: UpdateLevelConfigBody
): Promise<TResponseRequest<LevelConfig>> => {
  return httpRequest.patch<LevelConfig>({
    url: `${config.apiBaseUrl}/bo/levels/configs/${encodeURIComponent(String(level))}`,
    body,
  });
};

/* ── Promotion Conditions ──────────────────────────────────── */

export const getPromotionConditionsService = async (): Promise<TResponseRequest<PromotionCondition[]>> => {
  return httpRequest.get<PromotionCondition[]>({url: `${config.apiBaseUrl}/bo/levels/conditions`});
};

export const updatePromotionConditionService = async (
  conditionId: string,
  body: UpdatePromotionConditionBody
): Promise<TResponseRequest<PromotionCondition>> => {
  return httpRequest.patch<PromotionCondition>({
    url: `${config.apiBaseUrl}/bo/levels/conditions/${encodeURIComponent(conditionId)}`,
    body,
  });
};

/* ── Country Level Limits ──────────────────────────────────── */

export const getCountryLevelLimitsService = async (
  countryId: string
): Promise<TResponseRequest<CountryLevelLimit[]>> => {
  return httpRequest.get<CountryLevelLimit[]>({
    url: `${config.apiBaseUrl}/bo/levels/country-limits/${encodeURIComponent(countryId)}`,
  });
};

export const createCountryLevelLimitService = async (
  body: CreateCountryLevelLimitBody
): Promise<TResponseRequest<CountryLevelLimit>> => {
  return httpRequest.post<CountryLevelLimit>({
    url: `${config.apiBaseUrl}/bo/levels/country-limits`,
    body,
  });
};

export const updateCountryLevelLimitService = async (
  limitId: string,
  body: UpdateCountryLevelLimitBody
): Promise<TResponseRequest<CountryLevelLimit>> => {
  return httpRequest.patch<CountryLevelLimit>({
    url: `${config.apiBaseUrl}/bo/levels/country-limits/${encodeURIComponent(limitId)}`,
    body,
  });
};

export const deleteCountryLevelLimitService = async (
  limitId: string
): Promise<TResponseRequest<{deleted: boolean}>> => {
  return httpRequest.delete<{deleted: boolean}>({
    url: `${config.apiBaseUrl}/bo/levels/country-limits/${encodeURIComponent(limitId)}`,
    body: {},
  });
};
